import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Upload, Image as ImageIcon, Trophy } from 'lucide-react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { getFullImageUrl,uploadImage } from '../../utils/imageUpload';
import rankService from '../../services/rankService';
import toast from 'react-hot-toast';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b8b8d1;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #ff6b6b;
  }
`;

const ModalBody = styled.div`
  padding: 25px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  grid-column: ${props => props.fullWidth ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  display: block;
  color: #b8b8d1;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const InputField = styled(Input)`
  width: 100%;
`;

const ImageUploadContainer = styled.div`
  border: 2px dashed #2d2d44;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #00d4ff;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.05) 0%, transparent 100%);
  }
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImagePlaceholder = styled.div`
  color: white;
  font-size: 48px;
  opacity: 0.7;
`;

const UploadText = styled.div`
  color: #b8b8d1;
  font-size: 16px;
  margin-bottom: 10px;
`;

const UploadSubtext = styled.div`
  color: #6c6c8a;
  font-size: 14px;
`;

const FileInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #4ecdc4;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  padding: 25px;
  border-top: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const RankModal = ({ 
  show, 
  rank, 
  onClose, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    minimumScore: 0,
    maximumScore: 0,
    rankUpPrize: 0,
    picture: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (show) {
      if (rank) {
        setFormData({
          id: rank.id || '',
          name: rank.name || '',
          minimumScore: rank.minimumScore || 0,
          maximumScore: rank.maximumScore || 0,
          rankUpPrize: rank.rankUpPrize || 0,
          picture: rank.picture || ''
        });
      } else {
        setFormData({
          name: '',
          minimumScore: 0,
          maximumScore: 0,
          rankUpPrize: 0,
          picture: ''
        });
      }
      setError('');
      setSuccess('');
    }
  }, [show, rank]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');
    
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await uploadImage(file, 'rank');
console.log(response);
      if (!response) {
        throw new Error(`Server responded with ${response.status}`);
      }
      if (response) {
        setFormData(prev => ({
          ...prev,
          picture: response
        }));
        setSuccess('تصویر با موفقیت آپلود شد');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('خطا در آپلود تصویر');
      }
    } catch (err) {
      setError(err.message || 'خطا در آپلود تصویر');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.minimumScore || !formData.maximumScore) {
      setError('لطفاً تمام فیلدهای الزامی را پر کنید');
      return;
    }

    if (Number(formData.minimumScore) >= Number(formData.maximumScore)) {
      setError('حداقل امتیاز باید کمتر از حداکثر امتیاز باشد');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (rank) {
        await rankService.updateRank(formData);
        toast.success('رتبه با موفقیت ویرایش شد');
      } else {
        await rankService.createRank(formData);
        toast.success('رتبه با موفقیت ایجاد شد');
      }
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'خطا در ذخیره رتبه');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            <Trophy size={24} />
            {rank ? 'ویرایش رتبه' : 'افزودن رتبه جدید'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <form onSubmit={handleSubmit}>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <FormGroup fullWidth>
              <Label>نام رتبه *</Label>
              <InputField
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="نام رتبه را وارد کنید"
                required
              />
            </FormGroup>

            <FormGrid>
              <FormGroup>
                <Label>حداقل امتیاز *</Label>
                <InputField
                  type="number"
                  name="minimumScore"
                  value={formData.minimumScore}
                  onChange={handleChange}
                  placeholder="حداقل امتیاز"
                  min="0"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>حداکثر امتیاز *</Label>
                <InputField
                  type="number"
                  name="maximumScore"
                  value={formData.maximumScore}
                  onChange={handleChange}
                  placeholder="حداکثر امتیاز"
                  min="0"
                  required
                />
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <Label>جایزه ارتقا (تومان)</Label>
              <InputField
                type="number"
                name="rankUpPrize"
                value={formData.rankUpPrize}
                onChange={handleChange}
                placeholder="جایزه ارتقا"
                min="0"
              />
            </FormGroup>

            <FormGroup fullWidth>
              <Label>تصویر رتبه</Label>
              <ImageUploadContainer onClick={() => document.getElementById('rank-image-upload').click()}>
                {formData.picture ? (
                  <ImagePreview>
                    <img src={getFullImageUrl(formData.picture)} alt="Rank preview" />
                  </ImagePreview>
                ) : (
                  <ImagePlaceholder>
                    <ImageIcon size={48} />
                  </ImagePlaceholder>
                )}
                <UploadText>
                  {uploading ? 'در حال آپلود...' : 'برای آپلود تصویر کلیک کنید'}
                </UploadText>
                <UploadSubtext>
                  فرمت‌های مجاز: JPG, PNG, GIF, WebP (حداکثر 5MB)
                </UploadSubtext>
                <FileInput
                  id="rank-image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </ImageUploadContainer>
            </FormGroup>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            disabled={loading}
            icon={<X size={16} />}
          >
            انصراف
          </Button>
          <Button 
            type="submit" 
            variant="primary" 
            onClick={handleSubmit}
            disabled={loading || uploading}
            icon={<Trophy size={16} />}
          >
            {loading ? 'در حال ذخیره...' : (rank ? 'ویرایش رتبه' : 'ایجاد رتبه')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default RankModal;
